export type SupportedTickerExchange = 'binance' | 'deribit';

export interface NormalizedTicker {
  exchange: SupportedTickerExchange | undefined;
  timestamp: number | string | undefined;
  instrument_name: string | undefined;
  mark_price: number | undefined;
  index_price: number | undefined;
  last_price: number | undefined;
  funding_rate: number | undefined;
  next_funding_time: number | string | undefined;
  open_interest: number | undefined;
  high: number | undefined;
  low: number | undefined;
  volume: number | undefined;
  best_bid_price: number | undefined;
  best_ask_price: number | undefined;
  // Optional compatibility field used by Token Data UI when available.
  price_change: number | undefined;
}

const EMPTY_TICKER: NormalizedTicker = {
  exchange: undefined,
  timestamp: undefined,
  instrument_name: undefined,
  mark_price: undefined,
  index_price: undefined,
  last_price: undefined,
  funding_rate: undefined,
  next_funding_time: undefined,
  open_interest: undefined,
  high: undefined,
  low: undefined,
  volume: undefined,
  best_bid_price: undefined,
  best_ask_price: undefined,
  price_change: undefined,
};

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return undefined;
};

const toText = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
};

const toTimestamp = (value: unknown): number | string | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? trimmed : undefined;
  }

  return undefined;
};

const firstDefinedNumber = (...values: unknown[]) => {
  for (const value of values) {
    const parsed = toNumber(value);
    if (parsed !== undefined) return parsed;
  }

  return undefined;
};

const normalizeDeribitTicker = (payload: any): Partial<NormalizedTicker> => {
  const stats = payload?.stats ?? {};

  return {
    timestamp: toTimestamp(payload?.timestamp),
    instrument_name: toText(payload?.instrument_name),
    mark_price: toNumber(payload?.mark_price),
    index_price: toNumber(payload?.index_price),
    last_price: toNumber(payload?.last_price),
    funding_rate: firstDefinedNumber(payload?.current_funding, payload?.funding_8h),
    open_interest: toNumber(payload?.open_interest),
    high: toNumber(stats?.high),
    low: toNumber(stats?.low),
    volume: firstDefinedNumber(stats?.volume, stats?.volume_usd),
    best_bid_price: toNumber(payload?.best_bid_price),
    best_ask_price: toNumber(payload?.best_ask_price),
    price_change: toNumber(stats?.price_change),
  };
};

const normalizeBinanceTicker = (payload: any): Partial<NormalizedTicker> => {
  return {
    timestamp: toTimestamp(payload?.E),
    instrument_name: toText(payload?.s),
    mark_price: toNumber(payload?.p),
    index_price: toNumber(payload?.i),
    last_price: toNumber(payload?.ap),
    funding_rate: toNumber(payload?.r),
    next_funding_time: toTimestamp(payload?.T),
    price_change: toNumber(payload?.P),
  };
};

const TICKER_HANDLERS: Record<
  SupportedTickerExchange,
  (payload: any) => Partial<NormalizedTicker>
> = {
  deribit: normalizeDeribitTicker,
  binance: normalizeBinanceTicker,
};

const detectTickerExchange = (data: any): SupportedTickerExchange | undefined => {
  if (!data || typeof data !== 'object') return undefined;

  if (data.binance !== undefined) return 'binance';
  if (data.deribit !== undefined) return 'deribit';

  return undefined;
};

export const normalizeTickerMessage = (message: any): NormalizedTicker => {
  const data = message?.params?.data;
  const exchange = detectTickerExchange(data);

  if (!exchange) {
    return { ...EMPTY_TICKER };
  }

  const handler = TICKER_HANDLERS[exchange];
  const normalized = handler(data?.[exchange]);

  return {
    ...EMPTY_TICKER,
    exchange,
    ...normalized,
  };
};
